import { useState, useEffect } from "react";
import peacefullyLogo from "@/assets/peacefully-logo-white.png";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthProvider";
import { toast } from "sonner";

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { session, signOut } = useAuth();

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Signed out");
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
  { label: "Home", path: "/" },
  { label: "Destinations", path: "/destinations" },
  { label: "How It Works", path: "/work-with-me" },
  { label: "About", path: "/about" },
  { label: "Blog", path: "/blog" }];


  const isActive = (path: string) => location.pathname === path.split("#")[0] && !path.includes("#");

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? "bg-[hsl(222,64%,11%)]/95 backdrop-blur-lg border-b border-[hsl(46,30%,25%)]" : "bg-transparent"}`
      }>
      
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* ── Wordmark ─────────────────────────────────── */}
          {/* ── Wordmark ─────────────────────────────────── */}
          <Link to="/" className="flex items-center gap-4 group" aria-label="Peacefully Ever After Travels — Home">
            <div className="flex items-center">
              <img
                src={peacefullyLogo}
                alt="Peacefully Ever After Travels"
                className="h-10 w-auto opacity-90 transition-smooth group-hover:opacity-100" />
              
            </div>
            <div className="h-8 w-[1px] bg-gold/20" />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-[1.05rem] font-semibold text-white tracking-tight leading-tight group-hover:text-gold transition-smooth">
                Peacefully Ever After
              </span>
              <span className="font-cinzel text-[0.5rem] text-gold tracking-[0.38em] uppercase mt-0.5">Travel</span>
            </div>
          </Link>

          {/* ── Desktop nav ──────────────────────────────── */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
            <Link
              key={link.label}
              to={link.path}
              className={`font-cinzel text-[0.6rem] tracking-[0.18em] uppercase transition-smooth ${isActive(link.path) ? "text-gold" : "text-foreground/60 hover:text-foreground"}`
              }>
              
                {link.label}
              </Link>
            )}

            {session &&
            <>
                <Link
                to="/admin"
                className="font-cinzel text-[0.6rem] tracking-[0.18em] uppercase text-foreground/40 hover:text-foreground/70 transition-smooth">
                
                  Admin
                </Link>
                <button
                onClick={handleSignOut}
                className="font-cinzel text-[0.6rem] tracking-[0.18em] uppercase text-foreground/40 hover:text-foreground/70 transition-smooth">
                
                  Sign Out
                </button>
              </>
            }

            {/* CTA */}
            <a
              href="https://book.peacefullyeveraftertravel.com"
              className="relative font-cinzel text-[0.58rem] tracking-[0.2em] uppercase px-5 py-2.5 text-[hsl(222,64%,10%)] bg-gold hover:bg-[hsl(46,80%,60%)] transition-smooth shadow-gold-sm rounded-[2px] overflow-hidden group">
              
              <span className="relative z-10">Start Planning</span>
              <span className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-smooth" />
            </a>
          </div>

          {/* ── Mobile toggle ─────────────────────────────── */}
          <button
            className="md:hidden p-2 text-foreground"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle navigation menu">
            
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* ── Mobile menu ───────────────────────────────────── */}
        {isMobileMenuOpen &&
        <div className="md:hidden pb-6 border-t border-border/50 bg-[hsl(222,64%,11%)] animate-fade-in">
            <div className="pt-4 space-y-1">
              {navLinks.map((link) =>
            <Link
              key={link.label}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block py-3 px-2 font-cinzel text-[0.65rem] tracking-[0.2em] uppercase border-b border-border/30 transition-smooth ${isActive(link.path) ? "text-gold" : "text-foreground/60 hover:text-foreground"}`
              }>
              
                  {link.label}
                </Link>
            )}
              <div className="pt-4">
                <a
                href="https://book.peacefullyeveraftertravel.com"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-center font-cinzel text-[0.6rem] tracking-[0.22em] uppercase py-3 px-5 bg-gold text-[hsl(222,64%,10%)] rounded-[2px]">
                
                  Start Planning
                </a>
              </div>
              {session &&
            <div className="pt-2 space-y-1">
                  <Link
                to="/admin"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 px-2 font-cinzel text-[0.6rem] tracking-[0.18em] uppercase text-foreground/40">
                
                    Admin
                  </Link>
                  <button
                onClick={handleSignOut}
                className="block w-full text-left py-3 px-2 font-cinzel text-[0.6rem] tracking-[0.18em] uppercase text-foreground/40">
                
                    Sign Out
                  </button>
                </div>
            }
            </div>
          </div>
        }
      </div>
    </nav>);

};